import { useMemo, type ReactNode } from 'react'
import { useForm } from '@inertiajs/react'
import { admin_import_path, admin_imports_path } from '@/lib/routes'
import { Button } from '@/components/ui/Button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog'
import { nodeLabel, type DataSource, type OrgNode } from './types'

const inputClass = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm'

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h3>
      <div className="grid grid-cols-2 items-start gap-4">{children}</div>
    </section>
  )
}

function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={wide ? 'col-span-2' : undefined}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  )
}

export default function DataSourceModal({
  dataSource,
  onClose,
  processorClasses,
  sheetProcessorClasses,
  targetModels,
  maxResyncLookbackDays,
  orgNodes,
}: {
  dataSource?: DataSource | null
  onClose: () => void
  processorClasses: string[]
  sheetProcessorClasses: string[]
  targetModels: string[]
  maxResyncLookbackDays: number
  orgNodes: OrgNode[]
}) {
  const isEdit = !!dataSource
  const { data, setData, post, patch, processing } = useForm({
    name: dataSource?.name || '',
    source_type: dataSource?.source_type || 'metabase',
    source_identifier: dataSource?.source_identifier || '',
    sync_strategy: dataSource?.sync_strategy || 'full',
    sync_interval: dataSource?.sync_interval || 'daily',
    incremental_sync_date: dataSource?.incremental_sync_date || '',
    resync_lookback_days: dataSource?.resync_lookback_days ?? 0,
    frequent_sync_days: dataSource?.frequent_sync_days ?? null,
    target_model: dataSource?.target_model || '',
    processor_class: dataSource?.processor_class || '',
    org_node_id: dataSource?.org_node_id || '',
  })

  const nodesById = useMemo(
    () => Object.fromEntries(orgNodes.map((n) => [n.id, n])),
    [orgNodes]
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isEdit) {
      patch(admin_import_path(dataSource!.id), { onSuccess: onClose })
    } else {
      post(admin_imports_path(), { onSuccess: onClose })
    }
  }

  const incremental = data.sync_strategy === 'incremental'
  const googleSheet = data.source_type === 'google_sheet'
  const processorOptions = googleSheet ? sheetProcessorClasses : processorClasses

  // A Google Sheet is always read whole and replaced (snapshot), and only
  // sheet processors can read its rows.
  const changeSourceType = (sourceType: string) => {
    const sheet = sourceType === 'google_sheet'
    const options = sheet ? sheetProcessorClasses : processorClasses
    setData((prev) => ({
      ...prev,
      source_type: sourceType,
      sync_strategy: sheet ? 'snapshot' : prev.sync_strategy,
      processor_class: options.includes(prev.processor_class) ? prev.processor_class : '',
    }))
  }

  // The body scrolls inside a modal capped at the window's height, so the
  // title and the Cancel / Update buttons always stay on screen.
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 p-0" aria-describedby={undefined}>
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>{isEdit ? 'Edit Data Source' : 'New Data Source'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
          <div className="space-y-6 overflow-y-auto px-6 py-5">
            <Section title="Source">
              <Field label="Name">
                <input type="text" value={data.name} onChange={(e) => setData('name', e.target.value)}
                  className={inputClass} placeholder="e.g. Products" required />
              </Field>
              <Field label="Source Type">
                {isEdit ? (
                  <p className="py-2 text-sm">{googleSheet ? 'Google Sheet' : 'Metabase'}</p>
                ) : (
                  <select value={data.source_type} onChange={(e) => changeSourceType(e.target.value)} className={inputClass}>
                    <option value="metabase">Metabase</option>
                    <option value="google_sheet">Google Sheet</option>
                  </select>
                )}
              </Field>
              {googleSheet ? (
                <Field label="Google Sheet Link" wide
                  hint="Open the sheet on the tab to import and copy the link from the address bar. The Google account Atlas sends email from (Integrations page) must be able to open the sheet.">
                  <input type="url" value={data.source_identifier}
                    onChange={(e) => setData('source_identifier', e.target.value)}
                    className={inputClass} placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=…" required />
                </Field>
              ) : (
                <Field label="Metabase Question ID">
                  <input type="text" value={data.source_identifier}
                    onChange={(e) => setData('source_identifier', e.target.value)}
                    className={inputClass} placeholder="e.g. 42" required />
                </Field>
              )}
            </Section>

            <Section title="Schedule">
              <Field label="Sync Strategy" hint={googleSheet ? 'A Google Sheet is read whole and replaces the table on every sync.' : undefined}>
                {googleSheet ? (
                  <p className="py-2 text-sm">Snapshot</p>
                ) : (
                  <select value={data.sync_strategy} onChange={(e) => setData('sync_strategy', e.target.value)} className={inputClass}>
                    <option value="full">Full Sync</option>
                    <option value="snapshot">Snapshot</option>
                    <option value="incremental">Incremental</option>
                    <option value="update_only">Update Only</option>
                  </select>
                )}
              </Field>
              {incremental && (
                <Field label="Sync From Date" hint="Data will be synced starting from the day after this date">
                  <input type="date" value={data.incremental_sync_date}
                    onChange={(e) => setData('incremental_sync_date', e.target.value)} className={inputClass} required />
                </Field>
              )}
              {incremental && (
                <Field label="Resync Lookback (days)"
                  hint="Re-pull this many recent days on every sync to catch retroactive changes (returns, corrections). 0 = only sync new dates.">
                  <input type="number" min={0} max={maxResyncLookbackDays} value={data.resync_lookback_days}
                    onChange={(e) => setData('resync_lookback_days', Number(e.target.value))} className={inputClass} />
                </Field>
              )}
              {incremental && (
                <Field label="Sync every 15 minutes (days)"
                  hint="Every 15 minutes, re-pull today and this many days before. Empty = off.">
                  <input type="number" min={0} max={30} value={data.frequent_sync_days ?? ''}
                    onChange={(e) => setData('frequent_sync_days', e.target.value === '' ? null : Number(e.target.value))}
                    className={inputClass} />
                </Field>
              )}
              <Field label="Sync Interval">
                <select value={data.sync_interval} onChange={(e) => setData('sync_interval', e.target.value)} className={inputClass}>
                  <option value="">Manual only</option>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                </select>
              </Field>
            </Section>

            <Section title="Where it goes">
              <Field label="Target Model">
                <select value={data.target_model} onChange={(e) => setData('target_model', e.target.value)} className={inputClass}>
                  <option value="">None</option>
                  {targetModels.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
                </select>
              </Field>
              <Field label="Processor Class">
                <select value={data.processor_class} onChange={(e) => setData('processor_class', e.target.value)} className={inputClass}>
                  <option value="">None</option>
                  {processorOptions.map((pc) => <option key={pc} value={pc}>{pc}</option>)}
                </select>
              </Field>
              <Field label="Anchor Node" wide
                hint="Scopes destructive re-syncs to this node's subtree. Set one per market when multiple sources feed the same table (e.g. per-country sales).">
                <select value={data.org_node_id} onChange={(e) => setData('org_node_id', e.target.value)} className={inputClass}>
                  <option value="">None</option>
                  {orgNodes.map((n) => <option key={n.id} value={n.id}>{nodeLabel(n, nodesById)}</option>)}
                </select>
              </Field>
            </Section>
          </div>
          <DialogFooter className="border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
